import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, TextField, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Paper, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Grid, Typography, InputAdornment, IconButton,
  Alert, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { itAssetAPI } from '../services/api';
import api from '../services/api'; // default export — used directly for master-data, same as other pages

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 2 }}>{children}</Box> : null;
}

// Shared Export/Import controls — used identically in Assets, Employees, and Allotments tabs.
function ImportExportBar({ exportFn, importFn, onImported, filename }) {
  const [importing, setImporting] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [importError, setImportError] = useState('');

  const handleExport = async () => {
    try {
      const res = await exportFn();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setImportError('Failed to export');
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await importFn(formData);
      setResult(res.data.data);
      setResultOpen(true);
      onImported();
    } catch (err) {
      setImportError(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {importError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setImportError('')}>{importError}</Alert>}
      <Button size="small" onClick={handleExport} sx={{ mr: 1 }}>Export</Button>
      <Button size="small" component="label" disabled={importing} sx={{ mr: 1 }}>
        {importing ? 'Importing...' : 'Import'}
        <input hidden type="file" accept=".xlsx,.xls"
          onChange={(e) => e.target.files[0] && handleImportFile(e.target.files[0])} />
      </Button>

      <Dialog open={resultOpen} onClose={() => setResultOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Import Results</DialogTitle>
        <DialogContent>
          {result && (
            <>
              <Alert severity={result.skipped > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                Imported {result.created} of {result.created + result.skipped} rows
                {result.skipped > 0 ? ` — ${result.skipped} skipped` : ''}
              </Alert>
              {result.errors?.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Row</TableCell><TableCell>Reason</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((e, i) => (
                      <TableRow key={i}><TableCell>{e.row}</TableCell><TableCell>{e.reason}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function UpdatePurchaseDatesButton({ onImported }) {
  const [uploading, setUploading] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await itAssetAPI.bulkUpdatePurchaseDates(formData);
      setResult(res.data.data);
      setResultOpen(true);
      onImported();
    } catch (err) {
      setResult({ created: 0, skipped: 0, errors: [{ row: '-', reason: err.response?.data?.message || 'Upload failed' }] });
      setResultOpen(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button size="small" component="label" disabled={uploading} sx={{ mr: 1 }}>
        {uploading ? 'Updating...' : 'Update Purchase Dates'}
        <input hidden type="file" accept=".xlsx,.xls"
          onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
      </Button>
      <Dialog open={resultOpen} onClose={() => setResultOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update Results</DialogTitle>
        <DialogContent>
          {result && (
            <>
              <Alert severity={result.skipped > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                Updated {result.created} of {result.created + result.skipped} rows
                {result.skipped > 0 ? ` — ${result.skipped} skipped` : ''}
              </Alert>
              {result.errors?.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Row</TableCell><TableCell>Reason</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((e, i) => (
                      <TableRow key={i}><TableCell>{e.row}</TableCell><TableCell>{e.reason}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function ITAssetsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">IT Assets</Typography>
        <Button variant="outlined" onClick={async () => {
          const res = await itAssetAPI.exportAll();
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'it-assets-full-report.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }}>
          Export Full Report
        </Button>
      </Box>
      <Tabs value={tab} onChange={(e, v) => setTab(v)}>
        <Tab label="Assets" />
        <Tab label="Employees" />
        <Tab label="Allotments" />
        <Tab label="Tracker" />
      </Tabs>

      <TabPanel value={tab} index={0}><AssetsTab /></TabPanel>
      <TabPanel value={tab} index={1}><EmployeesTab /></TabPanel>
      <TabPanel value={tab} index={2}><AllotmentsTab /></TabPanel>
      <TabPanel value={tab} index={3}><TrackerTab /></TabPanel>
    </Box>
  );
}

// ============ ASSETS TAB ============

function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const fileUrl = (path) => `${api.defaults.baseURL.replace('/api/v1', '')}/uploads/${path}`;
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ trackingType: 'serialized' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reallocateOpen, setReallocateOpen] = useState(false);
  const [reallocateAssetId, setReallocateAssetId] = useState(null);
  const [reallocateEmployees, setReallocateEmployees] = useState([]);
  const [reallocateForm, setReallocateForm] = useState({ allottedDate: new Date().toISOString().slice(0, 10) });
  const [reallocateSaving, setReallocateSaving] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyLabel, setNewCompanyLabel] = useState('');
  const [newCompanyIsOwn, setNewCompanyIsOwn] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await itAssetAPI.getAssets({ search, includeDeleted: showDeleted });
      setAssets(res.data.data.assets);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [search, showDeleted]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    loadAssetTypes();
    loadOwnerCompanies();
  }, []);

  const loadOwnerCompanies = () => {
    api.get('/master-data', { params: { category: 'asset_owner_company' } })
      .then((res) => setOwnerCompanies(res.data.data?.items || res.data.data || []))
      .catch(() => setOwnerCompanies([]));
  };

  const handleAddCompany = async () => {
    if (!newCompanyLabel.trim()) return;
    const value = newCompanyLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    try {
      await api.post('/master-data', {
        category: 'asset_owner_company',
        value,
        label: newCompanyLabel.trim(),
        sortOrder: ownerCompanies.length + 1,
        metadata: { isOwn: newCompanyIsOwn },
      });
      setNewCompanyLabel('');
      setNewCompanyIsOwn(true);
      setAddingCompany(false);
      loadOwnerCompanies();
      setForm((f) => ({ ...f, ownerCompany: newCompanyLabel.trim() }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add new company');
    }
  };

  // A company defaults to "ours" unless explicitly marked otherwise —
  // so nothing gets flagged until you deliberately mark it as external.
  const isCompanyOwn = (label) => {
    const entry = ownerCompanies.find((c) => c.label === label);
    return !entry || entry.metadata?.isOwn !== false;
  };

  // ASSETS TAB VERSION — returns null (no chip shown) when data is missing,
  // otherwise an OBJECT { label, color } for direct use in a <Chip>.
  const getWarrantyStatus = (asset) => {
    if (!asset.purchaseDate || !asset.invoicePath) return null;
    const months = asset.warrantyMonths || 12;
    const purchaseDate = new Date(asset.purchaseDate);
    const warrantyEnd = new Date(purchaseDate);
    warrantyEnd.setMonth(warrantyEnd.getMonth() + months);
    const daysLeft = Math.floor((warrantyEnd - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: 'Warranty Expired', color: 'error' };
    if (daysLeft <= 30) return { label: `Warranty: ${daysLeft}d left`, color: 'warning' };
    return { label: 'Under Warranty', color: 'success' };
  };

  const setCompanyOwnership = async (label, isOwn) => {
    const entry = ownerCompanies.find((c) => c.label === label);
    if (!entry) return;
    try {
      await api.patch(`/master-data/${entry.id}`, { metadata: { ...(entry.metadata || {}), isOwn } });
      loadOwnerCompanies();
    } catch (err) {
      setError('Failed to update company classification');
    }
  };

  const loadAssetTypes = () => {
    api.get('/master-data', { params: { category: 'it_asset_type' } })
      .then((res) => setAssetTypes(res.data.data?.items || res.data.data || []))
      .catch(() => setAssetTypes([]));
  };

  const handleAddType = async () => {
    if (!newTypeLabel.trim()) return;
    const value = newTypeLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    try {
      await api.post('/master-data', {
        category: 'it_asset_type',
        value,
        label: newTypeLabel.trim(),
        sortOrder: assetTypes.length + 1,
      });
      setNewTypeLabel('');
      setAddingType(false);
      loadAssetTypes();
      setForm((f) => ({ ...f, assetType: value }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add new asset type');
    }
  };

  const handleCreate = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await itAssetAPI.updateAsset(editingId, form);
        setSuccess('Asset updated successfully');
      } else {
        await itAssetAPI.createAsset(form);
        setSuccess('Asset added successfully');
      }
      setDialogOpen(false);
      setForm({ trackingType: 'serialized' });
      setEditingId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (asset) => {
    setEditingId(asset.id);
    setForm({
      assetType: asset.assetType,
      trackingType: asset.trackingType,
      model: asset.model,
      serialNumber: asset.serialNumber,
      assetTag: asset.assetTag,
      quantityTotal: asset.quantityTotal,
      purchaseAmount: asset.purchaseAmount,
      isRental: asset.isRental,
      vendorName: asset.vendorName,
      ownerCompany: asset.ownerCompany,
      purchaseDate: asset.purchaseDate,
      warrantyMonths: asset.warrantyMonths,
      rentalPrice: asset.rentalPrice,
      originalValue: asset.originalValue,
      rentStartDate: asset.rentStartDate,
      invoiceNumber: asset.invoiceNumber,
    });
    setDialogOpen(true);
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Delete this asset? It can be recovered later if needed.')) return;
    try {
      await itAssetAPI.deleteAsset(id);
      setSuccess('Asset deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete asset');
    }
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('PERMANENTLY delete this asset? This cannot be undone.')) return;
    try {
      await itAssetAPI.hardDeleteAsset(id);
      setSuccess('Asset permanently deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to permanently delete asset');
    }
  };

  const handleRestore = async (id) => {
    try {
      await itAssetAPI.restoreAsset(id);
      setSuccess('Asset restored');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore asset');
    }
  };

  const viewHistory = async (asset) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await itAssetAPI.getAssetHistory(asset.id);
      setHistoryData(res.data.data);
    } catch (err) {
      setError('Failed to load asset history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openReallocate = async (asset) => {
    setReallocateAssetId(asset.id);
    setReallocateForm({ allottedDate: new Date().toISOString().slice(0, 10) });
    setError('');
    try {
      const res = await itAssetAPI.getEmployees({});
      setReallocateEmployees(res.data.data.employees.filter((e) => e.status === 'active'));
    } catch (err) {
      setError('Failed to load employees');
    }
    setReallocateOpen(true);
  };

  const handleReallocate = async () => {
    if (reallocateSaving) return;
    if (!reallocateForm.newEmployeeId) {
      setError('Please select the new employee');
      return;
    }
    setReallocateSaving(true);
    setError('');
    try {
      await itAssetAPI.reallocateAsset(reallocateAssetId, reallocateForm);
      setSuccess('Asset reallocated successfully');
      setReallocateOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reallocate asset');
    } finally {
      setReallocateSaving(false);
    }
  };

  const handleFileUpload = async (assetId, file, type) => {
    const formData = new FormData();
    formData.append('document', file);
    try {
      if (type === 'invoice') {
        await itAssetAPI.uploadInvoice(assetId, formData);
        setSuccess('Invoice uploaded');
      } else {
        await itAssetAPI.uploadRentalAgreement(assetId, formData);
        setSuccess('Rental agreement uploaded');
      }
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    }
  };

  const statusChip = (asset) => {
    if (asset.trackingType === 'quantity') {
      return <Chip size="small" label={`${asset.quantityAvailable}/${asset.quantityTotal} available`} />;
    }
    const color = asset.status === 'available' ? 'success' : asset.status === 'allotted' ? 'warning' : 'default';
    return <Chip size="small" color={color} label={asset.status} />;
  };

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={8}>
          <TextField
            fullWidth size="small" placeholder="Search by serial, tag, model..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
          <ImportExportBar
            exportFn={itAssetAPI.exportAssets}
            importFn={itAssetAPI.importAssets}
            onImported={load}
            filename="it-assets.xlsx"
          />
          <UpdatePurchaseDatesButton onImported={load} />
          <Button size="small" onClick={() => setShowDeleted(!showDeleted)} sx={{ mr: 1 }}>
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditingId(null); setForm({ trackingType: 'serialized' }); setDialogOpen(true); }}>
            Add Asset
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell><TableCell>Model</TableCell>
              <TableCell>Serial / Tag</TableCell><TableCell>Status</TableCell>
              <TableCell>Currently With</TableCell>
              <TableCell>Documents</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : assets.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No assets found</TableCell></TableRow>
            ) : (
              assets.map((a) => {
                const warranty = getWarrantyStatus(a);
                return (
                <TableRow key={a.id}>
                  <TableCell>
                    {a.assetType}
                    {a.isRental && <Chip size="small" label="Rental" color="info" sx={{ ml: 1 }} />}
                    {a.ownerCompany && (
                      <Chip size="small" label={a.ownerCompany}
                        color={isCompanyOwn(a.ownerCompany) ? 'default' : 'warning'}
                        sx={{ ml: 1 }} />
                    )}
                    {warranty && (
                      <Chip size="small" label={warranty.label} color={warranty.color} sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{a.model || '-'}</TableCell>
                  <TableCell>{a.serialNumber || a.assetTag || '-'}</TableCell>
                  <TableCell>{statusChip(a)}</TableCell>
                  <TableCell>{a.allotments?.[0]?.employee?.employeeName || '-'}</TableCell>
                  <TableCell>
                    {a.invoicePath ? (
                      <a href={fileUrl(a.invoicePath)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <Chip size="small" color="success"
                          label={a.invoiceNumber ? `Invoice: ${a.invoiceNumber}` : 'Invoice ✓'}
                          sx={{ mr: 0.5, cursor: 'pointer' }} />
                      </a>
                    ) : (
                      <IconButton size="small" component="label" title="Upload Invoice">
                        📄
                        <input hidden type="file" accept="image/*,.pdf"
                          onChange={(e) => e.target.files[0] && handleFileUpload(a.id, e.target.files[0], 'invoice')} />
                      </IconButton>
                    )}
                    {a.isRental && (
                      a.rentalAgreementPath ? (
                        <a href={fileUrl(a.rentalAgreementPath)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <Chip size="small" color="info" label="Agreement ✓" sx={{ cursor: 'pointer' }} />
                        </a>
                      ) : (
                        <IconButton size="small" component="label" title="Upload Rental Agreement">
                          📝
                          <input hidden type="file" accept="image/*,.pdf"
                            onChange={(e) => e.target.files[0] && handleFileUpload(a.id, e.target.files[0], 'agreement')} />
                        </IconButton>
                      )
                    )}
                  </TableCell>
                  <TableCell>{a.invoiceNumber || '-'}</TableCell>
                  <TableCell>
                    {a.deletedAt ? (
                      <>
                        <Chip size="small" label="Deleted" sx={{ mr: 1 }} />
                        <IconButton size="small" onClick={() => handleRestore(a.id)} title="Restore">♻️</IconButton>
                        <IconButton size="small" onClick={() => handleHardDelete(a.id)} title="Permanently delete" sx={{ color: 'error.main' }}>⛔</IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" onClick={() => openEdit(a)} title="Edit">✏️</IconButton>
                        <IconButton size="small" onClick={() => handleSoftDelete(a.id)} title="Delete">🗑️</IconButton>
                        <IconButton size="small" onClick={() => viewHistory(a)} title="History">🕒</IconButton>
                        {a.trackingType === 'serialized' && a.status === 'allotted' && (
                          <IconButton size="small" onClick={() => openReallocate(a)} title="Reallocate">🔁</IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingId(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          <TextField select label="Asset Type" value={form.assetType || ''}
            onChange={(e) => setForm({ ...form, assetType: e.target.value })}>
            {assetTypes.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            <MenuItem value="__add_new__" onClick={(e) => { e.preventDefault(); setAddingType(true); }}>
              + Add new type
            </MenuItem>
          </TextField>
          {addingType && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small" fullWidth label="New Type Name" value={newTypeLabel}
                onChange={(e) => setNewTypeLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddType()}
              />
              <Button variant="contained" onClick={handleAddType}>Add</Button>
              <Button onClick={() => { setAddingType(false); setNewTypeLabel(''); }}>Cancel</Button>
            </Box>
          )}
          <TextField select label="Tracking" value={form.trackingType}
            onChange={(e) => setForm({ ...form, trackingType: e.target.value })}>
            <MenuItem value="serialized">Serialized (one physical unit)</MenuItem>
            <MenuItem value="quantity">Quantity (stock count)</MenuItem>
          </TextField>
          <TextField select label="Owner Company" value={form.ownerCompany || ''}
            onChange={(e) => setForm({ ...form, ownerCompany: e.target.value })}>
            <MenuItem value="">— Unspecified —</MenuItem>
            {ownerCompanies.map((c) => <MenuItem key={c.value} value={c.label}>{c.label}</MenuItem>)}
            <MenuItem value="__add_new__" onClick={(e) => { e.preventDefault(); setAddingCompany(true); }}>
              + Add new company
            </MenuItem>
          </TextField>
          {form.ownerCompany && !addingCompany && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">{form.ownerCompany} is:</Typography>
              <Button size="small" variant={isCompanyOwn(form.ownerCompany) ? 'contained' : 'outlined'}
                onClick={() => setCompanyOwnership(form.ownerCompany, true)}>Ours</Button>
              <Button size="small" variant={!isCompanyOwn(form.ownerCompany) ? 'contained' : 'outlined'} color="warning"
                onClick={() => setCompanyOwnership(form.ownerCompany, false)}>Not Ours</Button>
            </Box>
          )}
          {addingCompany && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small" fullWidth label="New Company Name" value={newCompanyLabel}
                onChange={(e) => setNewCompanyLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCompany()}
              />
              <Button size="small" variant={newCompanyIsOwn ? 'contained' : 'outlined'} onClick={() => setNewCompanyIsOwn(true)}>Ours</Button>
              <Button size="small" variant={!newCompanyIsOwn ? 'contained' : 'outlined'} color="warning" onClick={() => setNewCompanyIsOwn(false)}>Not Ours</Button>
              <Button variant="contained" onClick={handleAddCompany}>Add</Button>
              <Button onClick={() => { setAddingCompany(false); setNewCompanyLabel(''); }}>Cancel</Button>
            </Box>
          )}
          <TextField label="Model" value={form.model || ''}
            onChange={(e) => setForm({ ...form, model: e.target.value })} />
          {form.trackingType === 'serialized' ? (
            <TextField label="Serial Number" value={form.serialNumber || ''}
              onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} />
          ) : (
            <TextField label="Quantity" type="number" value={form.quantityTotal || ''}
              onChange={(e) => setForm({ ...form, quantityTotal: Number(e.target.value) })} />
          )}
          {!form.isRental && (
            <TextField label="Purchase Amount" type="number" value={form.purchaseAmount || ''}
              onChange={(e) => setForm({ ...form, purchaseAmount: Number(e.target.value) })} />
          )}
          <TextField label="Purchase Date" type="date" value={form.purchaseDate || ''}
            onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Needed (along with an uploaded invoice) to calculate warranty status" />
          <TextField label="Warranty Period (months)" type="number" value={form.warrantyMonths ?? 12}
            onChange={(e) => setForm({ ...form, warrantyMonths: Number(e.target.value) })} />
          <TextField label="Invoice Number" value={form.invoiceNumber || ''}
            onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
            helperText="Auto-filled from the uploaded invoice PDF when possible — edit if it's wrong or missing" />
          <TextField select label="Ownership" value={form.isRental ? 'rental' : 'owned'}
            onChange={(e) => setForm({ ...form, isRental: e.target.value === 'rental' })}>
            <MenuItem value="owned">Owned</MenuItem>
            <MenuItem value="rental">Rental</MenuItem>
          </TextField>
          {form.isRental && (
            <>
              <TextField label="Vendor Name" value={form.vendorName || ''}
                onChange={(e) => setForm({ ...form, vendorName: e.target.value })} />
              <TextField label="Rental Price (per period)" type="number" value={form.rentalPrice || ''}
                onChange={(e) => setForm({ ...form, rentalPrice: Number(e.target.value) })} />
              <TextField label="Original Value" type="number" value={form.originalValue || ''}
                onChange={(e) => setForm({ ...form, originalValue: Number(e.target.value) })} />
              <TextField label="Rent Start Date" type="date" value={form.rentStartDate || ''}
                onChange={(e) => setForm({ ...form, rentStartDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setEditingId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Asset History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Allotted</TableCell>
                  <TableCell>Returned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData?.history?.length ? historyData.history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{h.employee?.employeeName} ({h.employee?.employeeCode})</TableCell>
                    <TableCell>{h.allottedDate}</TableCell>
                    <TableCell>{h.returnDate || (h.status === 'active' ? 'Ongoing' : '-')}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={3} align="center">No history yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reallocateOpen} onClose={() => setReallocateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reallocate Asset</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          <Alert severity="info">This will return the asset from its current holder and allot it to the new employee.</Alert>
          <TextField select label="New Employee" value={reallocateForm.newEmployeeId || ''}
            onChange={(e) => setReallocateForm({ ...reallocateForm, newEmployeeId: e.target.value })}>
            {reallocateEmployees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>{emp.employeeName} ({emp.employeeCode})</MenuItem>
            ))}
          </TextField>
          <TextField label="Allotment Date" type="date" value={reallocateForm.allottedDate || ''}
            onChange={(e) => setReallocateForm({ ...reallocateForm, allottedDate: e.target.value })}
            InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReallocateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleReallocate} disabled={reallocateSaving}>
            {reallocateSaving ? 'Saving...' : 'Reallocate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============ EMPLOYEES TAB ============

function EmployeesTab() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await itAssetAPI.getEmployees({ search, includeDeleted: showDeleted });
      setEmployees(res.data.data.employees);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [search, showDeleted]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await itAssetAPI.updateEmployee(editingId, form);
        setSuccess('Employee updated successfully');
      } else {
        await itAssetAPI.createEmployee(form);
        setSuccess('Employee added successfully');
      }
      setDialogOpen(false);
      setForm({});
      setEditingId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (employee) => {
    setEditingId(employee.id);
    setForm({
      employeeCode: employee.employeeCode,
      employeeName: employee.employeeName,
      department: employee.department,
      designation: employee.designation,
      contactNumber: employee.contactNumber,
      email: employee.email,
    });
    setDialogOpen(true);
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Delete this employee? Can be recovered later.')) return;
    try {
      await itAssetAPI.deleteEmployee(id);
      setSuccess('Employee deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete employee');
    }
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('PERMANENTLY delete this employee? Cannot be undone.')) return;
    try {
      await itAssetAPI.hardDeleteEmployee(id);
      setSuccess('Employee permanently deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to permanently delete employee');
    }
  };

  const handleRestore = async (id) => {
    try {
      await itAssetAPI.restoreEmployee(id);
      setSuccess('Employee restored');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore employee');
    }
  };

  const viewHistory = async (employee) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await itAssetAPI.getEmployeeHistory(employee.id);
      setHistoryData(res.data.data);
    } catch (err) {
      setError('Failed to load employee history');
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={8}>
          <TextField
            fullWidth size="small" placeholder="Search by name, employee ID, department..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
          <ImportExportBar
            exportFn={itAssetAPI.exportEmployees}
            importFn={itAssetAPI.importEmployees}
            onImported={load}
            filename="employees.xlsx"
          />
          <Button size="small" onClick={() => setShowDeleted(!showDeleted)} sx={{ mr: 1 }}>
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditingId(null); setForm({}); setDialogOpen(true); }}>
            Add Employee
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee ID</TableCell><TableCell>Name</TableCell>
              <TableCell>Department</TableCell><TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : employees.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No employees found</TableCell></TableRow>
            ) : (
              employees.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.employeeCode}</TableCell>
                  <TableCell>{e.employeeName}</TableCell>
                  <TableCell>{e.department || '-'}</TableCell>
                  <TableCell><Chip size="small" label={e.status} /></TableCell>
                  <TableCell>
                    {e.deletedAt ? (
                      <>
                        <Chip size="small" label="Deleted" sx={{ mr: 1 }} />
                        <IconButton size="small" onClick={() => handleRestore(e.id)} title="Restore">♻️</IconButton>
                        <IconButton size="small" onClick={() => handleHardDelete(e.id)} title="Permanently delete" sx={{ color: 'error.main' }}>⛔</IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" onClick={() => openEdit(e)} title="Edit">✏️</IconButton>
                        <IconButton size="small" onClick={() => handleSoftDelete(e.id)} title="Delete">🗑️</IconButton>
                        <IconButton size="small" onClick={() => viewHistory(e)} title="History">🕒</IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingId(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          <TextField label="Employee ID (HR code)" value={form.employeeCode || ''}
            onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} />
          <TextField label="Name" value={form.employeeName || ''}
            onChange={(e) => setForm({ ...form, employeeName: e.target.value })} />
          <TextField label="Department" value={form.department || ''}
            onChange={(e) => setForm({ ...form, department: e.target.value })} />
          <TextField label="Contact Number" value={form.contactNumber || ''}
            onChange={(e) => setForm({ ...form, contactNumber: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setEditingId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Employee Asset History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Allotted</TableCell>
                  <TableCell>Returned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData?.history?.length ? historyData.history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{h.asset?.assetType} — {h.asset?.serialNumber || h.asset?.model}</TableCell>
                    <TableCell>{h.allottedDate}</TableCell>
                    <TableCell>{h.returnDate || (h.status === 'active' ? 'Ongoing' : '-')}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={3} align="center">No history yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============ ALLOTMENTS TAB ============

function AllotmentsTab() {
  const [allotments, setAllotments] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ allottedDate: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await itAssetAPI.getAllotments({ search, status: statusFilter || undefined, includeDeleted: showDeleted });
      setAllotments(res.data.data.allotments);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load allotments');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, showDeleted]);

  useEffect(() => { load(); }, [load]);

  const openAllot = async () => {
    setEditingId(null);
    setForm({ allottedDate: new Date().toISOString().slice(0, 10) });
    setError('');
    try {
      const [assetsRes, employeesRes] = await Promise.all([
        itAssetAPI.getAssets({ status: 'available' }),
        itAssetAPI.getEmployees({}),
      ]);
      setAvailableAssets(
        assetsRes.data.data.assets.filter(
          (a) => a.trackingType === 'serialized' ? a.status === 'available' : a.quantityAvailable > 0
        )
      );
      setEmployees(employeesRes.data.data.employees.filter((e) => e.status === 'active'));
    } catch (err) {
      setError('Failed to load assets/employees for allotment');
    }
    setDialogOpen(true);
  };

  const openEditAllotment = (al) => {
    setEditingId(al.id);
    setForm({
      allottedDate: al.allottedDate,
      conditionAtAllotment: al.conditionAtAllotment || '',
      notes: al.notes || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await itAssetAPI.updateAllotment(editingId, form);
        setSuccess('Allotment updated');
      } else {
        if (!form.assetId || !form.employeeId) {
          setError('Please select both an employee and an asset');
          setSaving(false);
          return;
        }
        await itAssetAPI.createAllotment(form);
        setSuccess('Asset allotted successfully');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save allotment');
    } finally {
      setSaving(false);
    }
  };

  const handleReturn = async (id) => {
    setError('');
    try {
      await itAssetAPI.returnAllotment(id, { returnDate: new Date().toISOString().slice(0, 10) });
      setSuccess('Marked as returned');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark returned');
    }
  };

  const handleUpload = async (id, file) => {
    setError('');
    const formData = new FormData();
    formData.append('document', file);
    try {
      await itAssetAPI.uploadSignedDocument(id, formData);
      setSuccess('Signed form uploaded');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    }
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Delete this allotment record? Can be recovered later.')) return;
    try {
      await itAssetAPI.deleteAllotment(id);
      setSuccess('Allotment deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete allotment');
    }
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm('PERMANENTLY delete this allotment record? Cannot be undone.')) return;
    try {
      await itAssetAPI.hardDeleteAllotment(id);
      setSuccess('Allotment permanently deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to permanently delete allotment');
    }
  };

  const handleRestore = async (id) => {
    try {
      await itAssetAPI.restoreAllotment(id);
      setSuccess('Allotment restored');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore allotment');
    }
  };

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={5}>
          <TextField
            fullWidth size="small" placeholder="Search by employee name or ID..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField select fullWidth size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="returned">Returned</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
          <ImportExportBar
            exportFn={itAssetAPI.exportAllotments}
            importFn={itAssetAPI.importAllotments}
            onImported={load}
            filename="allotments.xlsx"
          />
          <Button size="small" onClick={() => setShowDeleted(!showDeleted)} sx={{ mr: 1 }}>
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openAllot}>
            Allot Asset
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell><TableCell>Asset</TableCell>
              <TableCell>Allotted</TableCell><TableCell>Returned</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Signed Form</TableCell><TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : allotments.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No allotments found</TableCell></TableRow>
            ) : (
              allotments.map((al) => (
                <TableRow key={al.id}>
                  <TableCell>{al.employee?.employeeName} ({al.employee?.employeeCode})</TableCell>
                  <TableCell>{al.asset?.assetType} — {al.asset?.serialNumber || al.asset?.model}</TableCell>
                  <TableCell>{al.allottedDate}</TableCell>
                  <TableCell>{al.returnDate || (al.status === 'active' ? 'Ongoing' : '-')}</TableCell>
                  <TableCell>{al.conditionAtAllotment || '-'}</TableCell>
                  <TableCell>
                    {al.signedDocumentPath ? (
                      <Chip size="small" color="success" label="Attached" />
                    ) : (
                      <IconButton size="small" component="label">
                        <UploadFileIcon fontSize="small" />
                        <input hidden type="file" accept="image/*,.pdf"
                          onChange={(e) => e.target.files[0] && handleUpload(al.id, e.target.files[0])} />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    {al.deletedAt ? (
                      <>
                        <Chip size="small" label="Deleted" sx={{ mr: 1 }} />
                        <IconButton size="small" onClick={() => handleRestore(al.id)} title="Restore">♻️</IconButton>
                        <IconButton size="small" onClick={() => handleHardDelete(al.id)} title="Permanently delete" sx={{ color: 'error.main' }}>⛔</IconButton>
                      </>
                    ) : (
                      <>
                        {al.status === 'active' && (
                          <Button size="small" onClick={() => handleReturn(al.id)}>Return</Button>
                        )}
                        <IconButton size="small" onClick={() => openEditAllotment(al)} title="Edit">✏️</IconButton>
                        <IconButton size="small" onClick={() => handleSoftDelete(al.id)} title="Delete">🗑️</IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit Allotment' : 'Allot Asset'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {!editingId && (
            <>
              <TextField select label="Employee" value={form.employeeId || ''}
                onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.employeeName} ({emp.employeeCode})</MenuItem>
                ))}
              </TextField>
              <TextField select label="Asset (available only)" value={form.assetId || ''}
                onChange={(e) => setForm({ ...form, assetId: e.target.value })}>
                {availableAssets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.assetType} — {a.serialNumber || a.model}
                    {a.trackingType === 'quantity' ? ` (${a.quantityAvailable} available)` : ''}
                  </MenuItem>
                ))}
              </TextField>
              {availableAssets.length === 0 && (
                <Alert severity="info">No available assets to allot right now.</Alert>
              )}
            </>
          )}
          <TextField label="Allotment Date" type="date" value={form.allottedDate || ''}
            onChange={(e) => setForm({ ...form, allottedDate: e.target.value })}
            InputLabelProps={{ shrink: true }} />
          <TextField label="Condition Notes" value={form.conditionAtAllotment || ''}
            onChange={(e) => setForm({ ...form, conditionAtAllotment: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============ TRACKER TAB ============
// Read-only consolidated view: every asset, its ownership/company, warranty, and who currently has it — sortable and searchable.

function TrackerTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('assetType');
  const [sortDir, setSortDir] = useState('asc');
  const [ownerCompanies, setOwnerCompanies] = useState([]);

  useEffect(() => {
    setLoading(true);
    itAssetAPI.getAssets({})
      .then((res) => setAssets(res.data.data.assets))
      .catch(() => setError('Failed to load tracker data'))
      .finally(() => setLoading(false));
    api.get('/master-data', { params: { category: 'asset_owner_company' } })
      .then((res) => setOwnerCompanies(res.data.data?.items || res.data.data || []))
      .catch(() => setOwnerCompanies([]));
  }, []);

  const isCompanyOwn = (label) => {
    const entry = ownerCompanies.find((c) => c.label === label);
    return !entry || entry.metadata?.isOwn !== false;
  };

  // TRACKER TAB VERSION — always returns a STRING (never null), since rows
  // rely on r.warranty.startsWith(...) further down. Never remove this
  // fallback behavior when editing.
  const getWarrantyStatus = (asset) => {
    if (!asset.purchaseDate || !asset.invoicePath) return 'Not Available';
    const months = asset.warrantyMonths || 12;
    const purchaseDate = new Date(asset.purchaseDate);
    const warrantyEnd = new Date(purchaseDate);
    warrantyEnd.setMonth(warrantyEnd.getMonth() + months);
    const daysLeft = Math.floor((warrantyEnd - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return 'Expired';
    if (daysLeft <= 30) return `Expiring (${daysLeft}d)`;
    return 'Under Warranty';
  };

  const rows = assets.map((a) => {
    const holder = a.allotments?.[0]?.employee;
    return {
      id: a.id,
      assetType: a.assetType,
      model: a.model || '-',
      serial: a.serialNumber || a.assetTag || '-',
      ownership: a.isRental ? 'Rental' : 'Owned',
      ownerCompany: a.ownerCompany || '',
      vendor: a.vendorName || '',
      warranty: getWarrantyStatus(a),
      employeeName: holder?.employeeName || '-',
      employeeCode: holder?.employeeCode || '-',
      status: a.status,
      allottedDate: a.allotments?.[0]?.allottedDate || '-',
    };
  });

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.assetType, r.model, r.serial, r.ownerCompany, r.vendor, r.employeeName, r.employeeCode]
      .some((v) => String(v).toLowerCase().includes(q));
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = String(a[sortKey] || '').toLowerCase();
    const bv = String(b[sortKey] || '').toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const columns = [
    { key: 'assetType', label: 'Type' },
    { key: 'model', label: 'Model' },
    { key: 'serial', label: 'Serial / Tag' },
    { key: 'ownership', label: 'Ownership' },
    { key: 'ownerCompany', label: 'Owner Company' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'warranty', label: 'Warranty' },
    { key: 'employeeName', label: 'Employee' },
    { key: 'employeeCode', label: 'Employee ID' },
    { key: 'status', label: 'Status' },
    { key: 'allottedDate', label: 'Allotted Date' },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TextField
        fullWidth size="small" placeholder="Search by type, model, serial, company, vendor, employee..."
        value={search} onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} onClick={() => toggleSort(col.key)} sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 700 }}>
                  {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : sorted.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} align="center">No assets found</TableCell></TableRow>
            ) : (
              sorted.map((r) => (
                <TableRow key={r.id} sx={r.ownerCompany && !isCompanyOwn(r.ownerCompany) ? { backgroundColor: '#fff8e1' } : {}}>
                  <TableCell>{r.assetType}</TableCell>
                  <TableCell>{r.model}</TableCell>
                  <TableCell>{r.serial}</TableCell>
                  <TableCell>
                    <Chip size="small" color={r.ownership === 'Rental' ? 'info' : 'default'} label={r.ownership} />
                  </TableCell>
                  <TableCell>
                    {r.ownerCompany ? (
                      <Chip size="small" color={isCompanyOwn(r.ownerCompany) ? 'default' : 'warning'} label={r.ownerCompany} />
                    ) : '-'}
                  </TableCell>
                  <TableCell>{r.vendor || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.warranty}
                      color={r.warranty === 'Expired' ? 'error' : r.warranty.startsWith('Expiring') ? 'warning' : r.warranty === 'Under Warranty' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>{r.employeeName}</TableCell>
                  <TableCell>{r.employeeCode}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell>{r.allottedDate}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}